import { FC, Fragment } from 'react'

import {
  Card,
  CustomPill,
  SquareDotLoadingIndicator,
  Tooltip,
} from '@joor/design-system'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'

import { Flex } from 'styles/components'
import { FORMAT_MM_DD_YYYY, formatDate } from 'utils/dates'
import { decimalWithCommas } from 'utils/formatters'
import { getIconNameForCard } from 'utils/images'

import Icon from 'components/Core/Icon/Icon'

import { useGetPaymentsHistory } from '../PublicInvoice.queries'
import { getCurrencyByCountry } from './helpers'
import { TransactionHistoryGrid } from './styles'
import { Payment, PaymentRecordStatus } from 'features/Orders/types'

interface PropsType {
  currencyCode: string
  shouldUseLocalCurrencies: boolean
  billingCountry: string
  orderId?: string
  accountId?: string
  externalPaymentId?: string
}
const EMPTY_PARAM_CHAR = '—'
const INFO_TEXT =
  'Conversion rates between price type and local currency are calculated at ' +
  'the time of transaction and may not represent current rates.'

const getPillColor = (status: PaymentRecordStatus) => {
  //TODO: change for types of BasePill when BasePill is exported
  const statusColors = {
    [PaymentRecordStatus.PAID]: 'var(--color-success-300)',
    [PaymentRecordStatus.SUCCESS]: 'var(--color-success-300)',
    [PaymentRecordStatus.REFUND]: 'var(--color-primary-300)',
    [PaymentRecordStatus.PROCESSING]: 'var(--color-warning-300)',
    [PaymentRecordStatus.REFUND_PROCESSING]: 'var(--color-warning-300)',
    [PaymentRecordStatus.PENDING]: 'var(--color-warning-300)',
    [PaymentRecordStatus.ERROR]: 'var(--color-error-300)',
    [PaymentRecordStatus.REFUND_ERROR]: 'var(--color-error-300)',
  }
  return statusColors[status]
}

/**
 * compliance-wise, 'Suspected Fraud' and 'Card has been reported lost or stolen'
 * should never been shown to the end user. This filter prevents that from happening.
 * we're expected to remove this check in the future when an actual fix in the BE
 * is implemented.
 * more info in ticket sc-182403 on shortcut.
 */
const forbiddenMessages = [
  'Suspected Fraud',
  'Card has been reported lost or stolen',
]
const filterErrorMessage = (errorMessage: string): string => {
  if (forbiddenMessages.includes(errorMessage)) {
    return 'Authentication Failed'
  }

  return errorMessage
}

const TransactionHistory: FC<PropsType> = ({
  orderId,
  accountId,
  externalPaymentId,
  currencyCode,
  shouldUseLocalCurrencies,
  billingCountry,
}) => {
  const { data: paymentData } = useGetPaymentsHistory({
    orderId,
    accountId,
    externalPaymentId,
  })

  if (!paymentData) {
    return (
      <Card
        title="JOOR Pay Transactions"
        {...(shouldUseLocalCurrencies && {
          infoText: INFO_TEXT,
        })}
      >
        <Flex justifyContent="center" alignItems="center">
          <SquareDotLoadingIndicator />
        </Flex>
      </Card>
    )
  }

  return (
    <Card
      title="JOOR Pay Transactions"
      {...(shouldUseLocalCurrencies && {
        infoText: INFO_TEXT,
      })}
    >
      {isEmpty(paymentData.payments) ? (
        <span>No payments have been made yet</span>
      ) : (
        <TransactionHistoryGrid
          gridTemplateColumns={`1.5fr repeat(${
            shouldUseLocalCurrencies ? 4 : 3
          }, 1fr)}`}
        >
          <span className="header with-bottom-border">Transaction Date</span>
          <span className="header with-bottom-border">Payment Method</span>
          <span className="header with-bottom-border">Status</span>
          {shouldUseLocalCurrencies && (
            <span className="header with-bottom-border text-right">
              Local Cur. Amount
            </span>
          )}
          <span className="header with-bottom-border text-right">
            Price Type Amount
          </span>
          {paymentData.payments.map(
            (
              {
                date,
                amount,
                currencyCode,
                methodName,
                status,
                localAmount,
                localCurrency,
                errorMessage,
                externalIdentifier,
              }: Payment,
              // TODO: try removing the js logic to pass the with-bottom-border class.
              // We could probably do that with just css
              idx: number,
            ) => {
              const iconName = getIconNameForCard(methodName)
              return (
                <Fragment key={`${externalIdentifier}-${idx}`}>
                  <span
                    className={classNames('cell', {
                      'with-bottom-border':
                        idx !== paymentData.payments.length - 1,
                    })}
                  >
                    {formatDate(date, `${FORMAT_MM_DD_YYYY} HH:mm:ss`, true)}
                  </span>
                  <span
                    className={classNames('cell icon-cell', {
                      'with-bottom-border':
                        idx !== paymentData.payments.length - 1,
                    })}
                  >
                    <Icon name={iconName} />
                  </span>
                  <span
                    className={classNames('cell', {
                      'with-bottom-border':
                        idx !== paymentData.payments.length - 1,
                      error: status === PaymentRecordStatus.ERROR,
                    })}
                  >
                    {errorMessage ? (
                      <Tooltip
                        content={filterErrorMessage(errorMessage)}
                        defaultPlacement="right"
                      >
                        <CustomPill
                          bgColor={getPillColor(status)}
                          color="var(--color-primary-900)"
                        >
                          {status}
                        </CustomPill>
                      </Tooltip>
                    ) : (
                      <CustomPill
                        bgColor={getPillColor(status)}
                        color="var(--color-primary-900)"
                      >
                        {status}
                      </CustomPill>
                    )}
                  </span>
                  {shouldUseLocalCurrencies && (
                    <span
                      className={classNames('cell text-right', {
                        'with-bottom-border':
                          idx !== paymentData.payments.length - 1,
                      })}
                    >
                      {localAmount
                        ? `${decimalWithCommas(localAmount)} ${
                            localCurrency || ''
                          }`
                        : EMPTY_PARAM_CHAR}
                    </span>
                  )}
                  <span
                    className={classNames('cell text-right', {
                      'with-bottom-border':
                        idx !== paymentData.payments.length - 1,
                    })}
                  >
                    {decimalWithCommas(amount)} {currencyCode}
                  </span>
                </Fragment>
              )
            },
          )}
          <div className="total-header cell">Paid</div>
          {shouldUseLocalCurrencies && (
            <div className="total text-right cell">{`${decimalWithCommas(
              paymentData?.localTotal,
            )} ${getCurrencyByCountry(billingCountry) || ''}`}</div>
          )}
          <div className="total text-right cell">{`${decimalWithCommas(
            paymentData?.total,
          )} ${currencyCode}`}</div>
        </TransactionHistoryGrid>
      )}
    </Card>
  )
}

export default TransactionHistory
